/* eslint-disable react-refresh/only-export-components --
 * Provider コンポーネントと、それを読む useMasterData を同一ファイルに置いている。
 * React の context は「Provider と consumer フックを1ファイルに閉じる」のが通例で、
 * 分けると context 本体を export するだけのファイルが増えて参照が追いにくくなる。
 * Fast Refresh はこのファイルを触ったときフルリロードになるが、Provider の編集は
 * どのみちアプリ全体の再マウントが要るため実害がない（router.tsx と同じ判断）。
 */
/**
 * MasterDataContext — テナント規模に比例して重くなるマスタ（企業 / ユーザ）の遅延取得
 * ================================================================================
 *
 * PRODUCT_DEV-652: これらは元々 root-loader の並列 8 本に含まれていた。root-loader は
 * react-router の loader なので**全部が返るまで最初の画面が一切描画されない**。
 * 8 本のうち `presets` / `dictionaries` / `realtimeConfigs` / `onpSettings` /
 * `globalDictionary` / `myDepartment` はテナント規模によらずほぼ一定サイズだが、
 * `companies`（企業マスタ全件）と `users`（在籍ユーザ全件）だけは利用者数・取引先数に
 * 比例して伸びる。つまり**大口テナントほど起動が遅くなる**構造になっていた。
 *
 * この 2 本を loader から外して「画面を出してから取りに行く」形にする。アプリシェル
 * （ヘッダ・ナビ・記録ボタン）はマスタを待たずに描画され、マスタを実際に使う一覧や
 * ピッカーだけが自分のローディング表示を出す。
 *
 * ## なぜ loader の `defer` / `Await` ではなくコンテキストなのか
 *
 * - 消費側が 20 箇所あり、`<Suspense>` + `<Await>` を全箇所に置くと構造が重くなる
 * - loader に置いたままだと**パス遷移のたびに再取得**される（`shouldRevalidate` は
 *   search のみの変化を止めるが、パスが変われば再検証が走る）。コンテキストならマウント
 *   時の 1 回で済み、遷移では再取得しない
 * - マスタ更新後の反映は既存実装がすべて `window.location.reload()` なので、
 *   「マウント時 1 回」で従来と等価になる
 *
 * ## 空配列とローディング中を混同しないこと
 *
 * `companies` / `users` は未取得中も `[]` を返す。`[]` を「0 件」と解釈して
 * 「登録がありません」と出すと**取得中に嘘を表示する**ので、そういう画面は必ず
 * `companiesLoading` / `usersLoading` を見て自分のローディング表示に倒すこと。
 * 単に userId → 名前を引くだけの箇所は、一覧自身のローディングに相乗りさせている。
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiGet } from "@shared-ui/utils/api";
import { API_COMPANIES, API_TENANT_USERS } from "@/utils/const";
import { useRootLoader } from "@/hooks/useRootLoader";
import type {
  TenantUsers,
  TenantUsersResponse,
} from "@shared-domain/auth-service/contract";
import type {
  CompaniesResponse,
  DDBCompany,
} from "@shared-domain/unizo/contract";

export interface MasterData {
  /** 企業マスター（未取得中は空配列。0 件と区別するには companiesLoading を見る） */
  companies: DDBCompany[];
  /** 企業マスターを取得中 */
  companiesLoading: boolean;
  /**
   * **人を並べる・選ばせる UI に出してよいユーザ**（未取得中は空配列。0 件と区別するには
   * usersLoading を見る）。顧客テナントでは当社スタッフが落ちる（PRODUCT_DEV-811）。
   *
   * 名簿の配列はこれしか公開していない。当社スタッフを含む生の一覧を配ると、次に
   * ピッカーを書く人が何も気づかずに当社スタッフを並べてしまうため。
   */
  users: TenantUsers[];
  /**
   * userId → 表示名。**過去の記録の作成者名を解決する用途だけ**に使う（PRODUCT_DEV-811）。
   *
   * こちらには当社スタッフも入っている。外すと、当社が顧客テナントで記録した商談・議事録の
   * 作成者名が「(ユーザ未登録)」になり、不具合と区別が付かなくなる。
   * **人を並べる用途に使わないこと**（配列ではなく Map なのはそのため）。
   */
  userNameById: ReadonlyMap<string, string>;
  /** 在籍ユーザを取得中 */
  usersLoading: boolean;
}

const EMPTY_COMPANIES: DDBCompany[] = [];
const EMPTY_USERS: TenantUsers[] = [];
const EMPTY_USER_NAMES: ReadonlyMap<string, string> = new Map();

const MasterDataContext = createContext<MasterData | null>(null);

/** 企業マスター一覧を取得（root-loader から移設。リクエストは従来と同一） */
async function fetchCompanies(tenantId: string): Promise<DDBCompany[]> {
  const params = new URLSearchParams({ tenantId, active: "true" });
  const response = await apiGet<CompaniesResponse>(
    `${API_COMPANIES}?${params.toString()}`,
  );
  return response.success ? response.companies : [];
}

/** ユーザーマスター一覧を取得（root-loader から移設。リクエストは従来と同一） */
async function fetchUsers(tenantId: string): Promise<TenantUsers[]> {
  const params = new URLSearchParams({ tenantId, active: "true" });
  const response = await apiGet<TenantUsersResponse>(
    `${API_TENANT_USERS}?${params.toString()}`,
  );
  return response.success ? response.users : [];
}

export const MasterDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { auth } = useRootLoader();
  // root-loader と同じ導出（403/404 ページでは空文字が入り、取得をスキップする）
  const tenantId = auth.currentTenantId || auth.memberships[0]?.tenantId || "";

  const [companies, setCompanies] = useState<DDBCompany[]>(EMPTY_COMPANIES);
  const [users, setUsers] = useState<TenantUsers[]>(EMPTY_USERS);
  // tenantId が無い（= 認証前 / エラーページ）ときは取りに行かないので loading も false
  const [companiesLoading, setCompaniesLoading] = useState(!!tenantId);
  const [usersLoading, setUsersLoading] = useState(!!tenantId);

  useEffect(() => {
    if (!tenantId) {
      setCompanies(EMPTY_COMPANIES);
      setUsers(EMPTY_USERS);
      setCompaniesLoading(false);
      setUsersLoading(false);
      return;
    }

    // テナント切替時に古い応答で新しいテナントのマスタを上書きしないためのガード。
    let aborted = false;

    setCompaniesLoading(true);
    setUsersLoading(true);

    // 2 本は独立に反映する（片方が遅くてももう片方の画面は先に出せる）。
    // root-loader 時代の allSettled と同じく、失敗しても空配列で継続しアプリは止めない。
    void fetchCompanies(tenantId)
      .catch(() => EMPTY_COMPANIES)
      .then((result) => {
        if (aborted) return;
        setCompanies(result);
        setCompaniesLoading(false);
      });

    void fetchUsers(tenantId)
      .catch(() => EMPTY_USERS)
      .then((result) => {
        if (aborted) return;
        setUsers(result);
        setUsersLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [tenantId]);

  // PRODUCT_DEV-811: API は当社スタッフも印付きで返す。並べてよい側と、名前を引くだけの
  // 側にここで一度だけ分ける（各画面で絞らせない）。
  const selectableUsers = useMemo(
    () => users.filter((u) => u.selectable !== false),
    [users],
  );
  const userNameById = useMemo(
    () => new Map(users.map((u) => [u.userId, u.name])),
    [users],
  );

  const value = useMemo<MasterData>(
    () => ({
      companies,
      companiesLoading,
      users: selectableUsers,
      userNameById,
      usersLoading,
    }),
    [companies, companiesLoading, selectableUsers, userNameById, usersLoading],
  );

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

/**
 * 企業マスター / 在籍ユーザを取得する。
 *
 * Provider の外（テストで個別にマウントした場合など）では「取得済みで 0 件」を返す。
 * ここで throw すると、マスタを使わない小さなコンポーネントの単体テストまで
 * Provider を要求することになるため。
 */
export function useMasterData(): MasterData {
  return (
    useContext(MasterDataContext) ?? {
      companies: EMPTY_COMPANIES,
      companiesLoading: false,
      users: EMPTY_USERS,
      userNameById: EMPTY_USER_NAMES,
      usersLoading: false,
    }
  );
}
